import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import {appPaths} from '@/config/appPaths';
import type {
  AfriexCurrency,
  AfriexCurrencyCode,
  CurrencySetting,
} from '@/types';
import {AfriexTransactionChannel, AfriexTransactionType} from '@/types';
import {capitalizeWords} from '@/utils/dashboard';

import {
  DetailRow,
  ExpandButton,
  ExpandedPanel,
  NotSet,
  PanelActions,
  PanelColumns,
  PanelSection,
  PillRow,
  StatePill,
} from '../ExpandedRowPanel';

type SettingsCurrencyTableProps = {
  list: AfriexCurrency[];
};

export default function SettingsCurrencyTable({
  list,
}: SettingsCurrencyTableProps) {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-12 px-4">&nbsp;</Th>
          <Th className="py-4 px-2 w-24">Name</Th>
          <Th className="py-4 px-2 w-24">Code</Th>
          <Th className="py-4 px-2 w-24">Symbol</Th>
          <Th className="py-4 px-2 w-24">Status</Th>
        </Tr>
      </Thead>

      <TBody className="bg-white divide-y divide-gray-100">
        {list.map((item: AfriexCurrency, index: number) => (
          <SettingsCurrencyRow
            key={item?.id ?? item?.code ?? index}
            item={item}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
}

const SettingsCurrencyRow = ({
  item,
  index,
}: {
  item: AfriexCurrency;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const currencyCode = item?.code?.toUpperCase() as AfriexCurrencyCode;
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <ExpandButton
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
            label={item?.name}
          />
        </Td>

        <Td className={'font-medium'}>{item?.name}</Td>
        <Td>{currencyCode}</Td>
        <Td>{item?.symbol || <NotSet />}</Td>
        <Td>{item?.isActive ? 'Active' : 'Inactive'}</Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={5} className="p-0 m-0">
            <ExpandedPanel>
              <CurrencyDetailPanel data={item} />
            </ExpandedPanel>
          </Td>
        </Tr>
      )}
    </>
  );
};

/**
 * `min / max` for one transaction type, from whichever of the two limits is
 * set. Rendered as a single line because they are read as a range, and shown
 * with an en dash placeholder on the missing side so a half-configured limit
 * is visibly half-configured rather than silently absent.
 */
const limitRange = (
  setting: CurrencySetting | undefined,
  type: AfriexTransactionType,
) => {
  const limits = setting?.[type]?.limits;
  const min = limits?.minTransactionLimit;
  const max = limits?.maxTransactionLimit;
  if (min === undefined && max === undefined) {
    return <NotSet />;
  }
  return `${min ?? '—'} / ${max ?? '—'}`;
};

/**
 * The processors configured for one transaction type, as
 * "Bank account: Korapay, Card: Payaza".
 *
 * A currency does not have one processor per transaction type — it has one per
 * channel — so this lists whatever channels are configured rather than
 * inventing a single value. Channels with no `currentProcessor` are the normal
 * case (there are 20-odd in the enum, a currency uses two or three), so they
 * are dropped rather than shown as empty.
 */
const processorSummary = (
  setting: CurrencySetting | undefined,
  type: AfriexTransactionType,
) => {
  const configured = Object.values(AfriexTransactionChannel)
    .map((channel) => ({
      channel,
      processor: setting?.[type]?.[channel]?.currentProcessor,
    }))
    .filter(({processor}) => !!processor);

  if (!configured.length) {
    return <NotSet />;
  }

  return configured
    .map(
      ({channel, processor}) =>
        `${capitalizeWords(channel.replace(/_/g, ' '))}: ${capitalizeWords(
          String(processor).replace(/_/g, ' '),
        )}`,
    )
    .join(', ');
};

/**
 * Read-only versions of what `Settings.tsx`, the two limit editors and
 * `CorridorKillSwitches.tsx` show on the Manage page.
 *
 * Note the kill switches here are the five `permissions` flags, not the
 * bank/mobile/card channels: the channels say *which processor* resolves an
 * account, while these are the switches that actually close a corridor, and
 * they are what `CorridorKillSwitches.tsx` edits.
 */
const CurrencyDetailPanel = ({data}: {data: AfriexCurrency}) => {
  const params = data?.params;
  const permissions = data?.permissions;

  return (
    <div className="w-full">
      <PanelColumns>
        <div>
          <PanelSection title="Fees">
            <DetailRow
              label="Withdrawal fee (bps)"
              value={params?.withdrawalFeeBasisPoints ?? <NotSet />}
            />
            <DetailRow
              label="Fixed withdrawal fee"
              value={params?.fixedWithdrawalFee ?? <NotSet />}
            />
            <DetailRow
              label="Fixed deposit fee"
              value={params?.fixedDepositFee ?? <NotSet />}
            />
            <DetailRow
              label="Fee structure"
              value={
                params?.feeStructure ? (
                  capitalizeWords(params.feeStructure)
                ) : (
                  <NotSet />
                )
              }
            />
          </PanelSection>

          <PanelSection title="Retail transaction limits" className="mt-5">
            <DetailRow
              label="Deposit min / max"
              value={limitRange(data?.user, AfriexTransactionType.DEPOSIT)}
            />
            <DetailRow
              label="Withdraw min / max"
              value={limitRange(data?.user, AfriexTransactionType.WITHDRAW)}
            />
            {/* The one retail limit the Manage page actually edits, so it is
                worth showing next to the amount range rather than only on the
                page that writes it. */}
            <DetailRow
              label="Deposit daily count"
              value={
                data?.user?.[AfriexTransactionType.DEPOSIT]?.limits
                  ?.transactionFrequencyThreshold ?? <NotSet />
              }
            />
            <DetailRow
              label="Withdraw daily count"
              value={
                data?.user?.[AfriexTransactionType.WITHDRAW]?.limits
                  ?.transactionFrequencyThreshold ?? <NotSet />
              }
            />
          </PanelSection>
        </div>

        <div>
          <PanelSection title="Business processors">
            <DetailRow
              label="Deposit processors"
              value={processorSummary(
                data?.business,
                AfriexTransactionType.DEPOSIT,
              )}
            />
            <DetailRow
              label="Withdraw processors"
              value={processorSummary(
                data?.business,
                AfriexTransactionType.WITHDRAW,
              )}
            />
          </PanelSection>

          <PanelSection title="Corridor kill switches" className="mt-5">
            <PillRow>
              <StatePill label="Deposits" allowed={permissions?.canDeposit} />
              <StatePill
                label="Withdrawals"
                allowed={permissions?.canWithdraw}
              />
              <StatePill
                label="Transfers out"
                allowed={permissions?.canTransferOutward}
              />
              <StatePill
                label="Transfers in"
                allowed={permissions?.canTransferInward}
              />
              <StatePill
                label="Referral payouts"
                allowed={permissions?.canRefer}
              />
            </PillRow>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
              A switch that has never been touched leaves the corridor open.
            </p>
          </PanelSection>
        </div>
      </PanelColumns>

      <PermissionsProvider permission={AfriexAdminPermission.CurrencyUpdate}>
        <PanelActions>
          <Button
            variant="solid"
            onClick={() =>
              window.open(
                `../${appPaths.config}/currencies/${data?.code}`,
                '_self',
              )
            }
          >
            Manage currency
          </Button>
        </PanelActions>
      </PermissionsProvider>
    </div>
  );
};
